/**
 * app.js
 * Obsługa formularza kontaktowego:
 *  - walidacja pól w locie,
 *  - wysyłka AJAX (fetch) bez przeładowania strony,
 *  - blokowanie formularza i loader podczas wysyłki,
 *  - podmiana formularza na ekran podziękowania po sukcesie.
 */


export function initContactForm() {
    'use strict';

    const form = document.getElementById('contact-form');
    const formWrapper = document.getElementById('form-wrapper');
    const thankYou = document.getElementById('thank-you');
    const submitButton = document.getElementById('submit-button');
    const submitButtonContent = document.getElementById('submit-button-content');
    const formError = document.getElementById('form-error');

    const fields = {
        name: document.getElementById('name'),
        email: document.getElementById('email'),
        phone: document.getElementById('phone'),
        message: document.getElementById('message'),
        consent: document.getElementById('consent'),
    };

    const csrfTokenInput = document.getElementById('csrf_token');
    const formTokenInput = document.getElementById('form_token');

    /**
     * Pobiera świeży token CSRF oraz podpisany token czasu z backendu (token.php)
     * i wstawia je do ukrytych pól formularza. Dopóki tokeny nie zostaną pobrane,
     * przycisk wysyłania jest zablokowany - bez nich send.php zawsze odrzuci żądanie.
     */
    async function loadFormTokens() {
        submitButton.disabled = true;

        try {
            const response = await fetch('token.php', {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!response.ok) {
                throw new Error(`Nieoczekiwana odpowiedź serwera: ${response.status}`);
            }

            const data = await response.json();

            csrfTokenInput.value = data.csrf_token ?? '';
            formTokenInput.value = data.form_token ?? '';

            submitButton.disabled = false;
        } catch (error) {
            // Formularz zostaje zablokowany - bez tokenu wysyłka i tak zawsze zakończy się błędem.
            formError.textContent = 'Nie udało się przygotować formularza. Odśwież stronę i spróbuj ponownie.';
            formError.classList.remove('hidden');
        }
    }

    // Prosty, ale solidny regex RFC-owy dla e-maili (uzupełnienie natywnej walidacji HTML5 type="email").
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    const ERROR_CLASSES = ['border-red-500'];

    /**
     * Ustawia stan błędu dla danego pola: czerwony border + komunikat pod polem.
     */
    function setFieldError(fieldName, message) {
        const input = fields[fieldName];
        const errorEl = document.getElementById(`${fieldName}-error`);

        if (input) {
            input.classList.add(...ERROR_CLASSES);
            input.setAttribute('aria-invalid', 'true');
        }

        if (errorEl) {
            errorEl.textContent = message;
            errorEl.classList.remove('hidden');
        }
    }

    /**
     * Czyści stan błędu dla danego pola.
     */
    function clearFieldError(fieldName) {
        const input = fields[fieldName];
        const errorEl = document.getElementById(`${fieldName}-error`);

        if (input) {
            input.classList.remove(...ERROR_CLASSES);
            input.removeAttribute('aria-invalid');
        }

        if (errorEl) {
            errorEl.textContent = '';
            errorEl.classList.add('hidden');
        }
    }

    function clearAllErrors() {
        Object.keys(fields).forEach(clearFieldError);
        formError.textContent = '';
        formError.classList.add('hidden');
    }

    /**
     * Walidacja pola "Imię i nazwisko".
     */
    function validateName() {
        const rawValue = fields.name.value.trim().replace(/\s+/g, ' ');

        if (rawValue === '') {
            setFieldError('name', 'Proszę podać imię i nazwisko.');
            return false;
        }

        if (rawValue.length < 3) {
            setFieldError('name', 'Imię i nazwisko musi mieć co najmniej 3 znaki.');
            return false;
        }

        clearFieldError('name');
        return true;
    }

    /**
     * Walidacja pola "E-mail".
     */
    function validateEmail() {
        const value = fields.email.value.trim();

        if (value === '') {
            setFieldError('email', 'Proszę podać adres e-mail.');
            return false;
        }

        const isHtml5Valid = fields.email.checkValidity();
        const isRegexValid = EMAIL_REGEX.test(value);

        if (!isHtml5Valid || !isRegexValid) {
            setFieldError('email', 'Podaj poprawny adres e-mail.');
            return false;
        }

        clearFieldError('email');
        return true;
    }

    /**
     * Walidacja checkboxa zgody.
     */
    function validateConsent() {
        if (!fields.consent.checked) {
            setFieldError('consent', 'Zgoda na przetwarzanie danych osobowych jest wymagana.');
            return false;
        }

        clearFieldError('consent');
        return true;
    }

    /**
     * Waliduje cały formularz. Zwraca true, jeśli wszystkie pola są poprawne.
     */
    function validateForm() {
        const isNameValid = validateName();
        const isEmailValid = validateEmail();
        const isConsentValid = validateConsent();

        return isNameValid && isEmailValid && isConsentValid;
    }

    // Pobierz tokeny bezpieczeństwa od razu po załadowaniu skryptu.
    loadFormTokens();

    // Błędy znikają na bieżąco, gdy użytkownik poprawia dane.
    fields.name.addEventListener('input', validateName);
    fields.email.addEventListener('input', validateEmail);
    fields.consent.addEventListener('change', validateConsent);

    /**
     * Przełącza formularz w stan "wysyłania": blokuje pola, pokazuje loader w przycisku.
     */
    function setSubmittingState(isSubmitting) {
        submitButton.disabled = isSubmitting;
        submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');

        Object.values(fields).forEach((input) => {
            input.disabled = isSubmitting;
        });

        if (isSubmitting) {
            submitButtonContent.innerHTML = `
                <svg class="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                <span class="sr-only">Wysyłanie wiadomości...</span>
            `;
        } else {
            submitButtonContent.innerHTML = `
                Wyślij wiadomość
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            `;
        }
    }

    /**
     * Pokazuje ekran podziękowania w miejscu formularza.
     */
    function showThankYouScreen() {
        formWrapper.classList.add('hidden');
        thankYou.classList.remove('hidden');
        thankYou.classList.add('!opacity-100');

        const heading = document.getElementById('thank-you-heading');
        if (heading) {
            heading.focus();
        }
    }

    /**
     * Wyświetla błędy zwrócone przez backend (np. błąd walidacji lub SMTP).
     */
    function displayServerErrors(errors) {
        if (!errors) {
            return;
        }

        Object.entries(errors).forEach(([fieldName, message]) => {
            if (fieldName === 'form') {
                formError.textContent = message;
                formError.classList.remove('hidden');
            } else if (fields[fieldName]) {
                setFieldError(fieldName, message);
            }
        });
    }

    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        // Zapobiega wielokrotnemu kliknięciu podczas trwającej wysyłki.
        if (submitButton.disabled) {
            return;
        }

        clearAllErrors();

        if (!validateForm()) {
            return;
        }

        const formData = new FormData(form);
        
        setSubmittingState(true);
        
        try {
            const response = await fetch('send.php', {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            // send.php zawsze zwraca JSON, niezależnie od kodu HTTP.
            const data = await response.json();

            if (data.success) {
                showThankYouScreen();
                return; // formularz zastąpiony ekranem podziękowania - nie trzeba już nic odblokowywać
            }

            displayServerErrors(data.errors);
        } catch (error) {
            // Błąd sieci / parsowania odpowiedzi.
            formError.textContent = 'Nie udało się wysłać wiadomości. Sprawdź połączenie z internetem i spróbuj ponownie.';
            formError.classList.remove('hidden');
        } finally {
            // Formularz odblokowujemy tylko wtedy, gdy nie doszło do sukcesu
            // (w przypadku sukcesu funkcja zakończyła się wcześniej przez `return`).
            setSubmittingState(false);
        }
    });
}
